import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Eye,
  Users,
  MousePointerClick,
  Percent,
  LogOut,
  RefreshCw,
  MapPin,
  Clock,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { api, getToken, setToken } from "@/lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_trading-revolution/artifacts/5op29ky3_REVOLUSI%20NUSATRADE%20PROFILE.png";

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: toISODate(from), to: toISODate(to) };
}

const MetricCard = ({ icon: Icon, label, value, accent = "#ED1C24", testId, suffix }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md transition hover:border-white/20"
  >
    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl opacity-30" style={{ background: accent }} />
    <div className="relative flex items-start justify-between">
      <div>
        <div
          className="text-[10px] uppercase tracking-[0.22em] text-white/55"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {label}
        </div>
        <div
          className="mt-2 text-4xl text-white"
          style={{ fontFamily: "'Bebas Neue', sans-serif" }}
        >
          {value}
          {suffix && <span className="ml-1 text-2xl text-white/60">{suffix}</span>}
        </div>
      </div>
      <div
        className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10"
        style={{ background: `${accent}20` }}
      >
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const range = defaultRange();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [region, setRegion] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/metrics", {
        params: { from, to, region: region === "all" ? undefined : region },
      });
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setToken(null);
        nav("/admin/login", { replace: true });
        return;
      }
      setErr("Gagal memuatkan data.");
    } finally {
      setLoading(false);
    }
  }, [from, to, region, nav]);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxHour = useMemo(
    () => Math.max(1, ...(data?.cta_by_hour?.map((h) => h.count) ?? [0])),
    [data]
  );
  const maxRegion = useMemo(
    () => Math.max(1, ...(data?.regions?.map((r) => r.count) ?? [0])),
    [data]
  );

  if (!getToken()) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    setToken(null);
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="relative min-h-screen bg-[#070405] text-white" data-testid="admin-dashboard">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_0%_0%,rgba(237,28,36,0.18),transparent_55%),radial-gradient(ellipse_at_100%_0%,rgba(33,64,154,0.18),transparent_55%),linear-gradient(180deg,#0a0507_0%,#070405_100%)]" />

      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Revolusi Nusatrade" className="h-9 w-9 rounded-full" />
            <div>
              <div
                className="text-base leading-tight tracking-[0.14em]"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                REVOLUSI NUSATRADE
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.22em] text-white/50"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Traffic Dashboard
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-white/75 transition hover:border-[#ED1C24]/50 hover:text-white"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <LogOut className="h-3.5 w-3.5" />
            Log Out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-md md:grid-cols-[1fr_1fr_1fr_auto]"
        >
          <div>
            <label
              className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/55"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <Calendar className="mr-1 inline h-3 w-3" /> Dari
            </label>
            <input
              data-testid="filter-from"
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#ED1C24]/50"
            />
          </div>
          <div>
            <label
              className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/55"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <Calendar className="mr-1 inline h-3 w-3" /> Sampai
            </label>
            <input
              data-testid="filter-to"
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#ED1C24]/50"
            />
          </div>
          <div>
            <label
              className="mb-1 block text-[10px] uppercase tracking-[0.22em] text-white/55"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <MapPin className="mr-1 inline h-3 w-3" /> Lokasi (Region)
            </label>
            <select
              data-testid="filter-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#ED1C24]/50"
            >
              <option value="all">Semua Lokasi</option>
              {data?.region_options?.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              data-testid="apply-filter-btn"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-[#ED1C24] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(237,28,36,0.7)] transition hover:scale-[1.02] disabled:opacity-60"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Terapkan
            </button>
            <button
              data-testid="refresh-btn"
              onClick={load}
              disabled={loading}
              title="Refresh"
              className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white/80 transition hover:border-[#FDBC3D]/50 hover:text-white disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </motion.div>

        {err && (
          <div className="mb-6 rounded-lg border border-[#ED1C24]/40 bg-[#ED1C24]/10 px-4 py-3 text-sm text-[#FDBC3D]">
            {err}
          </div>
        )}

        {/* Metric cards */}
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <MetricCard
            icon={Eye}
            label="Page Views"
            value={data?.page_views ?? "—"}
            accent="#ED1C24"
            testId="metric-pageviews"
          />
          <MetricCard
            icon={Users}
            label="Unique Visitors"
            value={data?.unique_visitors ?? "—"}
            accent="#FDBC3D"
            testId="metric-unique"
          />
          <MetricCard
            icon={MousePointerClick}
            label="CTA Clicks"
            value={data?.cta_clicks ?? "—"}
            accent="#21409A"
            testId="metric-cta"
          />
          <MetricCard
            icon={Percent}
            label="Conversion Rate"
            value={data?.conversion_rate ?? "—"}
            accent="#FDBC3D"
            testId="metric-conversion"
            suffix="%"
          />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* CTA by Hour */}
          <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md lg:col-span-2"
            data-testid="chart-cta-hourly"
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div
                  className="text-base"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}
                >
                  CTA CLICKS BY HOUR
                </div>
                <div
                  className="text-[11px] text-white/50"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <Clock className="mr-1 inline h-3 w-3" /> Malaysian Time (UTC+8)
                </div>
              </div>
              <TrendingUp className="h-4 w-4 text-[#FDBC3D]" />
            </div>
            <div className="flex h-56 items-end gap-1.5">
              {(data?.cta_by_hour ?? Array.from({ length: 24 }, (_, h) => ({ hour: h, count: 0 }))).map(
                (b) => {
                  const pct = maxHour ? (b.count / maxHour) * 100 : 0;
                  return (
                    <div
                      key={b.hour}
                      className="group flex flex-1 flex-col items-center justify-end"
                      data-testid={`hour-bar-${b.hour}`}
                    >
                      <div className="relative w-full">
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: `${Math.max(pct, 2)}%` }}
                          transition={{ duration: 0.6, ease: "easeOut", delay: b.hour * 0.015 }}
                          style={{
                            minHeight: 2,
                            background:
                              "linear-gradient(180deg,#FDBC3D 0%,#ED1C24 100%)",
                          }}
                          className="w-full rounded-t-md"
                        />
                        <div className="pointer-events-none absolute -top-7 left-1/2 hidden -translate-x-1/2 rounded-md bg-black/80 px-2 py-0.5 text-[10px] text-white group-hover:block">
                          {b.count}
                        </div>
                      </div>
                      <div className="mt-1 text-[9px] text-white/45">
                        {String(b.hour).padStart(2, "0")}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>

          {/* Top Regions */}
          <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md"
            data-testid="chart-regions"
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div
                  className="text-base"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}
                >
                  TOP REGIONS
                </div>
                <div
                  className="text-[11px] text-white/50"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Berdasarkan page views
                </div>
              </div>
              <MapPin className="h-4 w-4 text-[#FDBC3D]" />
            </div>
            <div className="space-y-3">
              {(data?.regions ?? []).length === 0 && (
                <div
                  className="rounded-lg border border-white/5 bg-black/20 px-3 py-6 text-center text-xs text-white/45"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Belum ada data lokasi untuk julat tarikh ini.
                </div>
              )}
              {(data?.regions ?? []).map((r, i) => {
                const pct = maxRegion ? (r.count / maxRegion) * 100 : 0;
                return (
                  <div key={r.region + i} data-testid={`region-row-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span
                        className="truncate text-white/85"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {r.region}
                      </span>
                      <span className="text-white/60">{r.count}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-white/5">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.7, ease: "easeOut", delay: i * 0.04 }}
                        className="h-full rounded-full"
                        style={{
                          background:
                            "linear-gradient(90deg,#ED1C24 0%,#FDBC3D 100%)",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div
          className="mt-6 text-right text-[10px] uppercase tracking-[0.2em] text-white/35"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {data?.generated_at
            ? `Last refreshed: ${new Date(data.generated_at).toLocaleString()}`
            : ""}
        </div>
      </main>
    </div>
  );
}
